
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Users, Phone, KeyRound } from "lucide-react";
import { Room } from "@/hooks/useRooms";
import { useDevices } from "@/hooks/useDevices";
import RoomServicesDialog from "./RoomServicesDialog";
import RoomDevicesDialog from "./RoomDevicesDialog";
import EditRoomDialog from "./EditRoomDialog";

interface RoomCardProps {
  room: Room;
  servicesCount?: number;
  onServicesUpdated?: () => void;
  showManagementButtons?: boolean;
}

const RoomCard = ({ room, servicesCount = 0, onServicesUpdated, showManagementButtons = false }: RoomCardProps) => {
  const { data: devices } = useDevices(room.provider_id);
  
  // Primary check: use has_device_access from room data
  // Fallback: check if there are active devices for this room
  const roomDevices = devices?.filter(device => 
    device.room_id === room.id && 
    device.is_active && 
    device.is_enabled
  ) || [];
  
  const hasDeviceAccess = room.has_device_access || roomDevices.length > 0;

  const handleDevicesUpdated = () => {
    if (onServicesUpdated) {
      onServicesUpdated();
    }
  };

  const handleRoomUpdated = () => {
    if (onServicesUpdated) {
      onServicesUpdated();
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg leading-tight flex-1 min-w-0">{room.name}</CardTitle>
            <div className="flex flex-col gap-1 flex-shrink-0">
              <Badge variant="secondary" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                {room.capacity}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1">
          {room.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-3">{room.description}</p>
          )}
          
          {room.address && (
            <div className="flex items-start text-sm text-gray-600 mb-2">
              <MapPin className="h-3 w-3 mr-2 mt-0.5 flex-shrink-0" />
              <span className="break-words">{room.address}</span>
            </div>
          )}

          {room.phone && (
            <div className="flex items-center text-sm text-gray-600 mb-3">
              <Phone className="h-3 w-3 mr-2 flex-shrink-0" />
              <span>{room.phone}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
            <div className="flex items-center">
              <Users className="h-3 w-3 mr-1" />
              <span>Capacity: {room.capacity}</span>
            </div>
            <div>
              {servicesCount} service{servicesCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Self Service Entry Badge */}
        {hasDeviceAccess && (
          <div className="mb-3">
            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
              <KeyRound className="h-3 w-3 mr-1" />
              Self Service Entry
            </Badge>
          </div>
        )}

        {showManagementButtons && (
          <div className="flex flex-col gap-2 mt-auto">
            <EditRoomDialog 
              room={room} 
              onRoomUpdated={handleRoomUpdated}
            />
            <RoomServicesDialog 
              room={room} 
              onServicesUpdated={onServicesUpdated || (() => {})}
            />
            <RoomDevicesDialog
              room={room}
              providerId={room.provider_id}
              onDevicesUpdated={handleDevicesUpdated}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoomCard;
